import React, { useState, KeyboardEvent } from "react";
import { Input, InputProps } from "@/components/Base/Input";
import { Tag } from "@/components/Common/Tag";
import { Tag as TagModel } from "@/types/v2/Tags";
import { cn, getTextColor } from "@/lib/utils";
import { DEFAULT_TAG_COLOR } from "@/lib/const";

export interface TagsProps extends Omit<InputProps, "value" | "onChange"> {
  tags: string[];
  onTagCreate?: (tag: string) => void;
  onTagRemove?: (index: number) => void;
  value?: string;
  onChange?: (value: string) => void;
  maxTags?: number;
  className?: string;
  tagClassName?: string;
  tagsContainerClassName?: string;
  canAddTags?: boolean;
  canRemoveTags?: boolean;
  suggestedTags?: string[];
  suggestedTagsContainerClassName?: string;
  maxSuggestedTags?: number;
  allTags?: TagModel[];
}

export const Tags = ({
  tags = [],
  onTagCreate,
  onTagRemove,
  value = "",
  onChange,
  maxTags,
  className,
  tagClassName,
  tagsContainerClassName,
  canAddTags = true,
  canRemoveTags = true,
  suggestedTags = [],
  suggestedTagsContainerClassName,
  maxSuggestedTags,
  allTags = [],
  ...props
}: TagsProps) => {
  const [inputValue, setInputValue] = useState(value);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();

      if (maxTags !== undefined && tags.length >= maxTags) {
        return;
      }
      onTagCreate?.(inputValue.trim());

      // Clear the input field
      setInputValue("");
      onChange?.("");
    }
  };

  const handleTagRemove = (index: number) => {
    onTagRemove?.(index);
  };

  const handleSuggestedTagClick = (tag: string) => {
    if (maxTags !== undefined && tags.length >= maxTags) {
      return;
    }

    if (tags.includes(tag)) {
      return;
    }

    onTagCreate?.(tag);

    // Clear the input field
    setInputValue("");
    onChange?.("");
  };

  // Filter suggested tags based on input value and exclude already selected tags
  const filteredSuggestedTags = suggestedTags
    .filter(
      (tag) =>
        !tags.includes(tag) &&
        (inputValue === "" ||
          tag.toLowerCase().includes(inputValue.toLowerCase()))
    )
    .slice(0, maxSuggestedTags);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {tags.length > 0 && (
        <div className={cn("flex flex-wrap gap-1", tagsContainerClassName)}>
          {tags.map((tag, index) => {
            const extendedTag = allTags.find(
              (t) => t.label.toLowerCase() === tag.toLowerCase()
            );
            return (
              <Tag
                key={`${tag}-${index}`}
                label={tag}
                onXClick={
                  canRemoveTags ? () => handleTagRemove(index) : undefined
                }
                className={cn(
                  "cursor-pointer hover:bg-primary hover:text-primary-foreground",
                  tagClassName
                )}
                style={
                  extendedTag
                    ? {
                        backgroundColor:
                          extendedTag.customColor ?? DEFAULT_TAG_COLOR,
                        color: getTextColor(
                          extendedTag.customColor ?? DEFAULT_TAG_COLOR
                        ),
                      }
                    : undefined
                }
              />
            );
          })}
        </div>
      )}
      {canAddTags && (
        <>
          <Input
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            {...props}
          />
          {filteredSuggestedTags.length > 0 && (
            <div
              className={cn(
                "mt-1 flex flex-wrap gap-1",
                suggestedTagsContainerClassName
              )}
            >
              {filteredSuggestedTags.map((tag) => {
                const extendedTag = allTags.find(
                  (t) => t.label.toLowerCase() === tag.toLowerCase()
                );
                return (
                  <Tag
                    key={tag}
                    label={tag}
                    className={cn(
                      "cursor-pointer hover:bg-primary hover:text-primary-foreground",
                      tagClassName
                    )}
                    onClick={() => handleSuggestedTagClick(tag)}
                    style={
                      extendedTag
                        ? {
                            backgroundColor:
                              extendedTag.customColor ?? DEFAULT_TAG_COLOR,
                            color: getTextColor(
                              extendedTag.customColor ?? DEFAULT_TAG_COLOR
                            ),
                          }
                        : undefined
                    }
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
